import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { Box, Grid, Card, CardContent, Typography, CircularProgress } from '@mui/material';

import { getAreas } from 'services/areas-service';
import { getMapCompanyBySlug } from 'services/company-service';

import { CompanyCard } from 'Components/CompanyCard';
import { MappingCard } from 'Components/MappingCard';
import { Main } from '../../Components/Main';
import Graphic from './Graphic';
import { getAssets } from 'services/assets-service';
import { getSystems } from 'services/systems-service';
import { getProcesses } from 'services/processes-service';
import { PageHelper } from '../../Components/PageHelper';

// Tour steps
const tourSteps = [
  {
    target: 'body',
    content: 'Bem-vindo ao Mapeamento Geral! Aqui você pode visualizar uma visão completa do mapeamento da sua organização, incluindo áreas, processos, sistemas e ativos.',
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="stats-cards"]',
    content: 'Estes cards mostram um resumo quantitativo de todos os elementos mapeados: Áreas, Processos, Sistemas e Ativos.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="map-visualization"]',
    content: 'Esta é a visualização gráfica do mapeamento corporativo. Você pode explorar as conexões entre os diferentes elementos da sua organização.',
  },
  {
    target: '[data-tour="map-legend"]',
    content: 'Esta legenda ajuda a identificar os diferentes tipos de elementos no mapa: Áreas (azul), Processos (rosa), Sistemas (ciano) e Ativos (verde).',
    disableScrolling: true,
  },
  {
    target: '[data-tour="map-info"]',
    content: 'Este painel mostra um resumo dos elementos mapeados com seus nomes.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="details-tables"]',
    content: 'Estas tabelas apresentam detalhes completos de cada elemento mapeado, incluindo descrições, responsáveis e criticidade.',
  },
  {
    target: 'body',
    content: 'Tour concluído! Explore o mapeamento para entender melhor a estrutura da sua organização.',
    placement: 'center',
  },
];

export const Map = () => {
  const selectedTenant = useSelector(useAuthTenant);
  const nodeTypes = useMemo(() => ({ company: CompanyCard, section: MappingCard }), []);

  const { data: companyMap, isSuccess: companyMapSuccess, isLoading: companyMapLoading } = useQuery(['getMapCompanyBySlug'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const response = await getMapCompanyBySlug(selectedTenant.slug);
    return response?.data;
  });

  const { data: companyAreas, isSuccess: areaSuccess, isLoading: areasLoading } = useQuery(['getAreas'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getAreas(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  const { data: companyProcesses, isSuccess: processSuccess, isLoading: processesLoading } = useQuery(['getProcesses'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getProcesses(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  const { data: companySystems, isSuccess: systemSuccess, isLoading: systemsLoading } = useQuery(['getSystems'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getSystems(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  const { data: companyAssets, isSuccess: assetSuccess, isLoading: assetsLoading } = useQuery(['getAssets'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getAssets(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  // Calcular estatísticas
  const areasCount = companyAreas?.length || 0;
  const processesCount = companyProcesses?.length || 0;
  const systemsCount = companySystems?.length || 0;
  const assetsCount = companyAssets?.length || 0;

  const isLoading = companyMapLoading || areasLoading || processesLoading || systemsLoading || assetsLoading;
  const allDataLoaded = companyMapSuccess && areaSuccess && processSuccess && systemSuccess && assetSuccess;

  // Criar dados estruturados para o mapa baseado nas áreas
  const mapData = useMemo(() => {
    if (!companyAreas?.length) return null;

    return companyAreas.map(area => ({
      id: area._id,
      name: area.name,
      description: area.description,
      user: area.user,
      criticity: area.criticity,
      processes: area.processes || [],
      systems: area.systems || [],
      assets: area.assets || [],
      type: 'area'
    }));
  }, [companyAreas]);

  return (
    <>
      {/* Helper Interativo */}
      <PageHelper 
        steps={tourSteps}
        tourKey="map-tour"
        autoStart={true}
        showButton={true}
      />
      
      <Main>
      <Box sx={{ p: 3 }}>
        {/* Cabeçalho */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 'bold' }}>
            Mapeamento Geral
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Mapeamento / Mapeamento Geral
          </Typography>
        </Box>

        {/* Cards de Estatísticas */}
        <Grid container spacing={3} sx={{ mb: 4 }} data-tour="stats-cards">
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              height: '120px'
            }}>
              <CardContent sx={{ textAlign: 'center', pt: 3 }}>
                <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {isLoading ? <CircularProgress size={24} color="inherit" /> : areasCount}
                </Typography>
                <Typography variant="body1">
                  Áreas mapeadas
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              height: '120px'
            }}>
              <CardContent sx={{ textAlign: 'center', pt: 3 }}>
                <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {isLoading ? <CircularProgress size={24} color="inherit" /> : processesCount}
                </Typography>
                <Typography variant="body1">
                  Processos mapeados
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              height: '120px'
            }}>
              <CardContent sx={{ textAlign: 'center', pt: 3 }}>
                <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {isLoading ? <CircularProgress size={24} color="inherit" /> : systemsCount}
                </Typography>
                <Typography variant="body1">
                  Sistemas mapeados
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              color: 'white',
              height: '120px'
            }}>
              <CardContent sx={{ textAlign: 'center', pt: 3 }}>
                <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {isLoading ? <CircularProgress size={24} color="inherit" /> : assetsCount}
                </Typography>
                <Typography variant="body1">
                  Ativos mapeados
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Mapa Corporativo */}
        <Card sx={{ minHeight: '800px', mb: 3 }} data-tour="map-visualization">
          <CardContent>
            <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 'bold' }}>
              Visualização do Mapa Corporativo
            </Typography>
            
            {isLoading ? (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '700px' 
              }}>
                <CircularProgress size={60} />
              </Box>
            ) : (
              <Box sx={{ 
                minHeight: '700px',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                position: 'relative',
                overflow: 'hidden'
              }}>
                {selectedTenant !== undefined && allDataLoaded && (
                  <Graphic
                    tenant={selectedTenant}
                    nodeTypes={nodeTypes}
                    result={companyMap}
                    areas={companyAreas}
                    processes={companyProcesses}
                    systems={companySystems}
                    assets={companyAssets}
                    mapData={mapData}
                  />
                )}
                
                {/* Informações adicionais sobre o mapa */}
                <Box sx={{ 
                  position: 'absolute', 
                  bottom: 16, 
                  left: 16, 
                  background: 'rgba(255,255,255,0.9)',
                  p: 2,
                  borderRadius: 1,
                  boxShadow: 1,
                  maxWidth: '300px',
                  maxHeight: '200px',
                  overflow: 'auto'
                }} data-tour="map-info">
                  <Typography variant="caption" display="block" sx={{ mb: 0.5, wordBreak: 'break-word' }}>
                    <strong>Áreas ({areasCount}):</strong> {companyAreas?.length > 3 
                      ? `${companyAreas.slice(0, 3).map(area => area.name).join(', ')}...` 
                      : companyAreas?.map(area => area.name).join(', ') || 'Nenhuma'}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mb: 0.5, wordBreak: 'break-word' }}>
                    <strong>Processos ({processesCount}):</strong> {companyProcesses?.length > 3 
                      ? `${companyProcesses.slice(0, 3).map(process => process.name).join(', ')}...` 
                      : companyProcesses?.map(process => process.name).join(', ') || 'Nenhum'}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mb: 0.5, wordBreak: 'break-word' }}>
                    <strong>Sistemas ({systemsCount}):</strong> {companySystems?.length > 3 
                      ? `${companySystems.slice(0, 3).map(system => system.name).join(', ')}...` 
                      : companySystems?.map(system => system.name).join(', ') || 'Nenhum'}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>
                    <strong>Ativos ({assetsCount}):</strong> {companyAssets?.length > 3 
                      ? `${companyAssets.slice(0, 3).map(asset => asset.name).join(', ')}...` 
                      : companyAssets?.map(asset => asset.name).join(', ') || 'Nenhum'}
                  </Typography>
                </Box>

                {/* Legenda dos tipos de elementos */}
                <Box sx={{ 
                  position: 'absolute', 
                  top: 16, 
                  right: 16, 
                  background: 'rgba(255,255,255,0.9)',
                  p: 2,
                  borderRadius: 1,
                  boxShadow: 1
                }} data-tour="map-legend">
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Legenda
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: '#2196f3', borderRadius: 1, mr: 1 }} />
                    <Typography variant="caption">Áreas de Negócio</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: '#e91e63', borderRadius: 1, mr: 1 }} />
                    <Typography variant="caption">Processos</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: '#00bcd4', borderRadius: 1, mr: 1 }} />
                    <Typography variant="caption">Sistemas</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: '#4caf50', borderRadius: 1, mr: 1 }} />
                    <Typography variant="caption">Ativos</Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Tabela de detalhes dos elementos mapeados */}
        <Grid container spacing={3} data-tour="details-tables">
          {/* Detalhes das Áreas */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 'bold', color: '#2196f3' }}>
                  Áreas de Negócio ({areasCount})
                </Typography>
                <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                  {companyAreas?.length > 0 ? (
                    companyAreas.map((area) => (
                      <Box key={area._id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, wordBreak: 'break-word' }}>
                          {area.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, wordBreak: 'break-word' }}>
                          {area.description?.length > 100 ? `${area.description.substring(0, 100)}...` : area.description}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>
                          <strong>Responsável:</strong> {area.user} | <strong>Criticidade:</strong> {area.criticity}
                        </Typography>
                        <Typography variant="caption" display="block">
                          <strong>Processos:</strong> {area.processes?.length || 0} | 
                          <strong>Sistemas:</strong> {area.systems?.length || 0} | 
                          <strong>Ativos:</strong> {area.assets?.length || 0}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography color="text.secondary">Nenhuma área mapeada</Typography>
                  )}

                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Detalhes dos Processos */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 'bold', color: '#e91e63' }}>
                  Processos ({processesCount})
                </Typography>
                <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                  {companyProcesses?.length > 0 ? (
                    companyProcesses.map((process) => (
                      <Box key={process._id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, wordBreak: 'break-word' }}>
                          {process.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, wordBreak: 'break-word' }}>
                          {process.description?.length > 100 ? `${process.description.substring(0, 100)}...` : process.description}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>
                          <strong>Responsável:</strong> {process.user} | <strong>Criticidade:</strong> {process.criticity}
                        </Typography>
                        <Typography variant="caption" display="block">
                          <strong>DPIA:</strong> {process.dpia ? 'Sim' : 'Não'} | 
                          <strong>LIA:</strong> {process.lia ? 'Sim' : 'Não'} | 
                          <strong>Dados:</strong> {process.dataMapping?.length || 0}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography color="text.secondary">Nenhum processo mapeado</Typography>
                  )}

                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Detalhes dos Sistemas */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 'bold', color: '#00bcd4' }}>
                  Sistemas ({systemsCount})
                </Typography>
                <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                  {companySystems?.length > 0 ? (
                    companySystems.map((system) => (
                      <Box key={system._id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, wordBreak: 'break-word' }}>
                          {system.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, wordBreak: 'break-word' }}>
                          {system.description?.length > 100 ? `${system.description.substring(0, 100)}...` : system.description}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>
                          <strong>Responsável:</strong> {system.user} | <strong>Criticidade:</strong> {system.criticity}
                        </Typography>
                        <Typography variant="caption" display="block">
                          <strong>SaaS:</strong> {system.saas ? 'Sim' : 'Não'} | 
                          <strong>Desenvolvimento Próprio:</strong> {system.developedInHouse ? 'Sim' : 'Não'}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography color="text.secondary">Nenhum sistema mapeado</Typography>
                  )}

                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Detalhes dos Ativos */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 'bold', color: '#4caf50' }}>
                  Ativos ({assetsCount})
                </Typography>
                <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                  {companyAssets?.length > 0 ? (
                    companyAssets.map((asset) => (
                      <Box key={asset._id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, wordBreak: 'break-word' }}>
                          {asset.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, wordBreak: 'break-word' }}>
                          {asset.description?.length > 100 ? `${asset.description.substring(0, 100)}...` : asset.description}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>
                          <strong>Responsável:</strong> {asset.user} | <strong>Criticidade:</strong> {asset.criticity}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>
                          <strong>Tipo:</strong> {asset.type} | 
                          <strong>PII:</strong> {asset.pii ? 'Sim' : 'Não'} | 
                          <strong>Local:</strong> {asset.location}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography color="text.secondary">Nenhum ativo mapeado</Typography>
                  )}

                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Main>
    </>
  );
};
